// Feature: docs/features/structured-coding-variance-analysis.md
// Feature: docs/features/coding-variance-ai-evaluation.md
// Feature: docs/features/coding-variance-preliminary-flag.md
// Feature: docs/features/structured-icd10-variance-analysis.md
// Feature: docs/features/icd10-variance-narrative-justification.md
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
  Box, Flex, Text, Badge, Spinner, Button, Heading, Container,
  Table, Thead, Tbody, Tr, Th, Td, useToast, Divider, VStack, HStack,
  Card, CardHeader, CardBody, Stat, StatLabel, StatNumber, StatGroup, SimpleGrid,
  Select, Grid, GridItem, Textarea, Tag, TagLabel, IconButton, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton
} from "@chakra-ui/react";
import { DownloadIcon, ViewIcon } from '@chakra-ui/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import ModernChat from './ModernChat';

export default function CodingVarianceReport() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState('');
  const [currentClaimType, setCurrentClaimType] = useState('pro');
  const [generationError, setGenerationError] = useState(null);
  
  // New state for case info and document viewer
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState('');
  const [docUrl, setDocUrl] = useState(null);
  const [isDocLoading, setIsDocLoading] = useState(false);

  // Modal state for file content
  const { isOpen: isTextModalOpen, onOpen: onTextModalOpen, onClose: onTextModalClose } = useDisclosure();
  const [selectedTextFile, setSelectedTextFile] = useState(null);

  const formatFileSize = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileDownload = async (file) => {
      try {
        const response = await callApi('/download_case_file', accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            case_id: currentCaseId,
            file_id: file.id 
          }),
        });
  
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = file.file_name;
          document.body.appendChild(a);
          a.click();
          
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }, 100);
        } else {
          console.error('Failed to download file');
        }
      } catch (error) {
        console.error('Download failed', error);
      }
  };

  // State for feedback and navigation
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [allReports, setAllReports] = useState([]);

  const { accessToken } = useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const hasLoadedFromUrl = useRef(false);

  const markdownStyles = {
    'p': { marginBottom: '0.5rem', whiteSpace: 'pre-wrap' },
    'ul': { paddingLeft: '1.5rem', marginBottom: '0.5rem' },
    'ol': { paddingLeft: '1.5rem', marginBottom: '0.5rem' },
    'li': { marginBottom: '0.25rem' },
    'strong': { fontWeight: 'bold' },
    'em': { fontStyle: 'italic' }
  };

  // Handle URL loading
  useEffect(() => {
    if (accessToken && !hasLoadedFromUrl.current) {
      const params = new URLSearchParams(location.search);
      const caseIdFromUrl = params.get('caseId');
      const claimTypeFromUrl = params.get('claimType') || 'pro';
      
      if (caseIdFromUrl) {
        setCurrentCaseId(caseIdFromUrl);
        setCurrentClaimType(claimTypeFromUrl);
        fetchReport(caseIdFromUrl, claimTypeFromUrl);
        hasLoadedFromUrl.current = true;
      }
    }
  }, [accessToken, location.search]);

  const fetchCaseData = useCallback(async (caseId) => {
    if (!caseId || !accessToken) return;
    try {
        const detailsPromise = callApi(`/get_case_details/${caseId}`, accessToken);
        const workflowPromise = callApi(`/workflow_status/${caseId}`, accessToken);

        const [detailsResponse, workflowResponse] = await Promise.all([detailsPromise, workflowPromise]);

        const detailsData = await detailsResponse.json();
        if (detailsResponse.ok) {
            setCaseFiles(detailsData.files || []);
        }

        const workflowData = await workflowResponse.json();
        if (workflowResponse.ok) {
            setWorkflowStatus(workflowData);
        }
    } catch (err) {
        console.error("Error fetching case data:", err);
    }
  }, [accessToken]);

  useEffect(() => {
    if (currentCaseId) {
      fetchCaseData(currentCaseId);
    }
  }, [currentCaseId, fetchCaseData]);

  const handleFileSelect = async (fileId) => {
    setSelectedFileId(fileId);
    
    if (!fileId) {
      if (docUrl) window.URL.revokeObjectURL(docUrl);
      setDocUrl(null);
      return;
    }

    setIsDocLoading(true);
    if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
    }
    setDocUrl(null);

    try {
        const response = await callApi('/download_case_file', accessToken, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                case_id: currentCaseId,
                file_id: fileId,
                disposition: 'inline'
            }),
        });

        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setDocUrl(url);
        } else {
            throw new Error('Failed to load file');
        }
    } catch (error) {
        console.error('Error loading file:', error);
        toast({
            title: 'Failed to load file',
            status: 'error',
            duration: 3000,
        });
    } finally {
        setIsDocLoading(false);
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
      }
    };
  }, [docUrl]);

  const fetchFeedback = useCallback(async (reportId) => {
    if (!reportId || !accessToken) return;
    try {
      const response = await callApi(`/api/coding-variance/feedback/${reportId}`, accessToken);
      const result = await response.json();
      if (result.status === 'success' && result.feedback) {
        setExistingFeedback(result.feedback);
        setFeedbackNotes(result.feedback.notes || '');
      } else {
        setExistingFeedback(null);
        setFeedbackNotes('');
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  }, [accessToken]);

  const fetchAllReports = useCallback(async () => {
    if (!accessToken) return;
    try {
      const response = await callApi('/api/coding-variance/all', accessToken);
      const result = await response.json();
      if (result.status === 'success') {
        setAllReports(result.reports || []);
      }
    } catch (error) {
      console.error('Error fetching all reports:', error);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchAllReports();
  }, [fetchAllReports]);

  const fetchReport = async (caseId, claimType) => {
    if (!caseId) return;
    setLoading(true);
    setGenerationError(null);
    try {
      const response = await callApi(`/api/coding-variance/case/${caseId}?claim_type=${claimType}`, accessToken);
      const result = await response.json();
      if (result.status === 'success') {
        setReportData(result.report);
        if (result.report && result.report.id) {
          fetchFeedback(result.report.id);
        }
      } else {
        toast({
          title: "Error fetching report",
          description: result.message || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching variance report:', error);
      toast({
        title: "Network Error",
        description: "Could not connect to server.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (!reportData || !reportData.id) return;
    
    setIsSavingFeedback(true);
    try {
      const response = await callApi('/api/coding-variance/feedback', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: reportData.id,
          notes: feedbackNotes
        })
      });
      
      const result = await response.json();
      if (result.status === 'success') {
        setExistingFeedback(result.feedback);
        toast({
          title: "Feedback Saved",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(result.message || 'Failed to save feedback');
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: "Error Saving Feedback",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleNextCase = () => {
    if (allReports.length === 0) return;
    
    const currentIndex = allReports.findIndex(
      r => r.external_case_id === currentCaseId && r.claim_type === currentClaimType
    );
    
    if (currentIndex >= 0 && currentIndex < allReports.length - 1) {
      const nextReport = allReports[currentIndex + 1];
      // Reset state for new case
      setReportData(null);
      setFeedbackNotes('');
      setExistingFeedback(null);
      setDocUrl(null);
      setSelectedFileId('');
      
      // Update URL and fetch
      navigate(`/coding-variance?caseId=${nextReport.external_case_id}&claimType=${nextReport.claim_type}`);
      setCurrentCaseId(nextReport.external_case_id);
      setCurrentClaimType(nextReport.claim_type);
      fetchReport(nextReport.external_case_id, nextReport.claim_type);
    } else {
      toast({
        title: "End of List",
        description: "There are no more variance reports to review.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const generateReport = async () => {
    if (!currentCaseId) {
      toast({
        title: "Case ID Required",
        description: "Please ensure a Case ID is provided in the URL.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setGenerating(true);
    setGenerationError(null);
    try {
      const response = await callApi('/api/coding-variance/generate', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ case_id: currentCaseId, claim_type: currentClaimType, run_ai: true })
      });
      const result = await response.json();
      
      if (result.status === 'success') {
        setReportData(result.report);
        toast({
          title: "Report Generated",
          description: "Successfully generated new variance report.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        setGenerationError(result.message || "Failed to generate report.");
        toast({
          title: "Generation Failed",
          description: result.message || "Failed to generate report.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error generating variance report:', error);
      toast({
        title: "Network Error",
        description: "Could not connect to server.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  const renderModifiers = (modifiers) => {
    if (!modifiers || modifiers.length === 0) return <Text fontSize="xs" color="gray.400">None</Text>;
    return (
      <Flex flexWrap="wrap" gap={1}>
        {modifiers.map((mod, idx) => (
          <Badge key={idx} colorScheme="gray" variant="outline" fontSize="xs">{mod}</Badge>
        ))}
      </Flex>
    );
  };

  const renderCPTSection = () => {
    if (!reportData || !reportData.cpt_variance) return null;
    const { exact_matches, modifier_mismatches, unit_mismatches, missing_in_prospect, extra_in_prospect } = reportData.cpt_variance;

    return (
      <Box mt={6}>
        {reportData.has_preliminary_codes && (
          <Box bg="orange.100" borderLeft="4px solid" borderColor="orange.500" p={4} mb={6} rounded="md">
            <Text fontWeight="bold" color="orange.800">⚠️ Preliminary Codes Detected</Text>
            <Text fontSize="sm" color="orange.900">
              This case contains preliminary Revedy codes that require manual review (e.g., missing units or duration issues). 
              The variance analysis below may change after the codes are finalized.
            </Text>
          </Box>
        )}
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size="md">CPT & Modifier Variance</Heading>
          {reportData.cpt_ai_evaluation?.triage_evaluation && (
            <Badge 
              colorScheme={reportData.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved ? 'blue' : 'orange'} 
              fontSize="md" 
              p={2} 
              rounded="md"
            >
              {reportData.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved ? 'FULLY AUTO-RESOLVED' : 'ACTIONABLE VARIANCE'}
            </Badge>
          )}
        </Flex>
        
        <StatGroup mb={6} bg="white" p={4} rounded="md" shadow="sm" borderWidth="1px">
          <Stat>
            <StatLabel color="green.600">Exact Matches</StatLabel>
            <StatNumber>{exact_matches?.length || 0}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="yellow.600">Modifier/Unit Mismatches</StatLabel>
            <StatNumber>{(modifier_mismatches?.length || 0) + (unit_mismatches?.length || 0)}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="blue.600">Missing (Revedy Found)</StatLabel>
            <StatNumber>{missing_in_prospect?.length || 0}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="red.600">Extra (Claim Billed)</StatLabel>
            <StatNumber>{extra_in_prospect?.length || 0}</StatNumber>
          </Stat>
        </StatGroup>

        <Grid templateColumns={{ base: "1fr", xl: "60% 40%" }} gap={6}>
          <GridItem>
            <Box bg="white" shadow="sm" rounded="lg" overflowX="auto" borderWidth="1px">
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th rowSpan={2} borderRightWidth="1px" verticalAlign="bottom">Status</Th>
                    <Th colSpan={3} textAlign="center" borderRightWidth="1px" bg="gray.100">Claim Data (CMS 1500)</Th>
                    <Th colSpan={3} textAlign="center" bg="blue.50">Revedy Output</Th>
                  </Tr>
                  <Tr>
                    {/* Claim */}
                    <Th>CPT</Th>
                    <Th>Modifiers</Th>
                    <Th borderRightWidth="1px">Units</Th>
                    {/* Revedy */}
                    <Th bg="blue.50">CPT</Th>
                    <Th bg="blue.50">Modifiers</Th>
                    <Th bg="blue.50">Units</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {/* Exact Matches */}
                  {exact_matches?.map((item, idx) => (
                    <Tr key={`exact-${idx}`} bg="green.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="green">Match</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px">{item.prospect.units}</Td>
                      <Td fontWeight="bold">
                        {item.revedy.cpt_code}
                        {item.revedy.type === 'preliminary' && (
                          <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                        )}
                      </Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td>{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Modifier Mismatches */}
                  {modifier_mismatches?.map((item, idx) => (
                    <Tr key={`mod-mis-${idx}`} bg="yellow.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="yellow">Mod Mismatch</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px">{item.prospect.units}</Td>
                      <Td fontWeight="bold">
                        {item.revedy.cpt_code}
                        {item.revedy.type === 'preliminary' && (
                          <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                        )}
                      </Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td>{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Unit Mismatches */}
                  {unit_mismatches?.map((item, idx) => (
                    <Tr key={`unit-mis-${idx}`} bg="orange.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="orange">Unit Mismatch</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px" fontWeight="bold" color="red.600">{item.prospect.units}</Td>
                      <Td fontWeight="bold">
                        {item.revedy.cpt_code}
                        {item.revedy.type === 'preliminary' && (
                          <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                        )}
                      </Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td fontWeight="bold" color="green.600">{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Extra in Prospect (Claim Only) */}
                  {extra_in_prospect?.map((item, idx) => (
                    <Tr key={`extra-${idx}`} bg="red.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="red">Claim Only</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px">{item.prospect.units}</Td>
                      <Td color="gray.400">-</Td>
                      <Td color="gray.400">-</Td>
                      <Td color="gray.400">-</Td>
                    </Tr>
                  ))}

                  {/* Missing in Prospect (Revedy Only) */}
                  {missing_in_prospect?.map((item, idx) => (
                    <Tr key={`missing-${idx}`} bg="blue.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="blue">Revedy Only</Badge></Td>
                      <Td color="gray.400">-</Td>
                      <Td color="gray.400">-</Td>
                      <Td borderRightWidth="1px" color="gray.400">-</Td>
                      <Td fontWeight="bold">
                        {item.revedy.cpt_code}
                        {item.revedy.type === 'preliminary' && (
                          <Badge ml={2} colorScheme="orange" fontSize="2xs">Needs Review</Badge>
                        )}
                      </Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td>{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {(!exact_matches?.length && !modifier_mismatches?.length && !unit_mismatches?.length && !missing_in_prospect?.length && !extra_in_prospect?.length) && (
                    <Tr>
                      <Td colSpan={7} textAlign="center" py={4} color="gray.500">No CPT data available.</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>

            <Box mt={6}>
              <Heading size="sm" mb={2}>CPT Variance Chat</Heading>
              <Text color="gray.600" fontSize="sm" mb={3}>
                Ask questions about the differences in CPT codes, modifiers, and units.
              </Text>
              <ModernChat 
                caseId={currentCaseId} 
                contextType="coding_variance" 
                height="400px"
                collapsedHeight="100px"
              />
            </Box>
          </GridItem>
          
          <GridItem>
            {reportData.cpt_ai_evaluation && reportData.cpt_ai_evaluation.differences && (
              <Box p={5} bg="white" shadow="sm" rounded="lg" borderWidth="1px" borderColor="purple.200" h="100%">
                <Heading size="sm" mb={4} color="purple.800">Structured Variance Analysis</Heading>
                
                {reportData.cpt_ai_evaluation.triage_evaluation && reportData.cpt_ai_evaluation.triage_evaluation.triage_summary && (
                  <Box p={4} bg={reportData.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved ? 'blue.50' : 'orange.50'} rounded="md" borderWidth="1px" borderColor={reportData.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved ? 'blue.200' : 'orange.200'} mb={6}>
                    <Text fontSize="sm" fontWeight="bold" color={reportData.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved ? 'blue.800' : 'orange.800'} mb={2}>
                      TRIAGE SUMMARY:
                    </Text>
                    <Text fontSize="sm" color={reportData.cpt_ai_evaluation.triage_evaluation.is_fully_auto_resolved ? 'blue.900' : 'orange.900'}>
                      {reportData.cpt_ai_evaluation.triage_evaluation.triage_summary}
                    </Text>
                  </Box>
                )}

                <VStack align="stretch" spacing={4}>
                  {reportData.cpt_ai_evaluation.differences.map((diff, idx) => (
                    <Box key={idx} p={4} bg="gray.50" rounded="md" borderWidth="1px" borderColor="gray.200">
                      <Grid templateColumns="1fr 1fr" gap={4} mb={3}>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Claim Billed</Text>
                          <Text fontSize="sm" fontWeight="medium">{diff.claim_cpt}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Revedy Recommended</Text>
                          <Text fontSize="sm" fontWeight="medium">{diff.revedy_cpt}</Text>
                        </Box>
                      </Grid>
                      
                      <Flex align="center" mb={2}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mr={2}>Who Was Right:</Text>
                        <Badge 
                          colorScheme={
                            diff.who_was_right?.toLowerCase() === 'revedy' ? 'blue' : 
                            diff.who_was_right?.toLowerCase() === 'claim' ? 'orange' : 
                            diff.who_was_right?.toLowerCase() === 'both' ? 'green' : 'red'
                          }
                        >
                          {diff.who_was_right?.toUpperCase()}
                        </Badge>
                      </Flex>
                      
                      <Box p={3} bg="white" rounded="md" borderWidth="1px" borderColor="gray.100">
                        <Text fontSize="sm" color="gray.700">{diff.explanation}</Text>
                      </Box>
                    </Box>
                  ))}
                  {reportData.cpt_ai_evaluation.differences.length === 0 && (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">No differences found to evaluate.</Text>
                  )}
                </VStack>
              </Box>
            )}
          </GridItem>
        </Grid>
      </Box>
    );
  };

  const normalizeCode = (code) => code?.replace(/\./g, '').toUpperCase();
  
  const getIcd10Color = (code, variance) => {
    if (!code || !variance) return "gray";
    const norm = normalizeCode(code);
    if (variance.exact_matches?.includes(norm)) return "green";
    if (variance.extra_in_prospect?.includes(norm)) return "yellow";
    if (variance.missing_in_prospect?.includes(norm)) return "blue";
    return "gray";
  };

  const renderICD10Section = () => {
    if (!reportData) return null;
    
    const prospectIcd10 = reportData.prospect_icd10_data || [];
    const revedyIcd10 = reportData.revedy_icd10_data || [];
    const variance = reportData.icd10_variance || {};

    // Sort prospect codes by pointer (A, B, C...)
    const sortedProspect = [...prospectIcd10].sort((a, b) => (a.pointer || '').localeCompare(b.pointer || ''));

    return (
      <Box mt={8} mb={8}>
        <Heading size="md" mb={4}>ICD-10 Codes & Sequencing</Heading>
        <Grid templateColumns={{ base: "1fr", xl: "60% 40%" }} gap={6}>
          <GridItem>
            <Flex gap={6} flexWrap="wrap">
              
              {/* Claim Data */}
              <Card flex="1" minW="300px" variant="outline" borderColor="gray.200">
                <CardHeader bg="gray.50" py={3} borderBottomWidth="1px">
                  <Heading size="sm" color="gray.700">Claim Data (CMS 1500)</Heading>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={2}>
                    {sortedProspect.map((item, idx) => {
                      const color = getIcd10Color(item.code, variance);
                      const inDoc = variance.codes_in_text?.includes(normalizeCode(item.code));
                      return (
                        <Flex key={idx} justify="space-between" align="center" p={2} bg={`${color}.50`} rounded="md" borderWidth="1px" borderColor={`${color}.200`}>
                          <Text fontWeight="bold" color={`${color}.700`}>Pointer {item.pointer}</Text>
                          <HStack>
                            {inDoc && <Badge colorScheme="green" fontSize="2xs" variant="outline">In Doc</Badge>}
                            <Badge colorScheme={color} fontSize="sm">{item.code}</Badge>
                          </HStack>
                        </Flex>
                      );
                    })}
                    {sortedProspect.length === 0 && <Text color="gray.500" fontSize="sm">No ICD-10 codes found on claim.</Text>}
                  </VStack>
                </CardBody>
              </Card>

              {/* Revedy Data */}
              <Card flex="1" minW="300px" variant="outline" borderColor="gray.200">
                <CardHeader bg="blue.50" py={3} borderBottomWidth="1px" borderColor="blue.100">
                  <Flex justify="space-between" align="center">
                    <Heading size="sm" color="blue.800">Revedy Output</Heading>
                    {reportData.revedy_icd10_source === 'nma_strict' && (
                      <Badge colorScheme="purple" variant="solid" fontSize="2xs">NMA Strict Rules</Badge>
                    )}
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={2}>
                    {revedyIcd10.map((code, idx) => {
                      const color = getIcd10Color(code, variance);
                      const inDoc = variance.codes_in_text?.includes(normalizeCode(code));
                      return (
                        <Flex key={idx} justify="space-between" align="center" p={2} bg={`${color}.50`} rounded="md" borderWidth="1px" borderColor={`${color}.200`}>
                          <Text fontWeight="bold" color={`${color}.700`}>Order {idx + 1}</Text>
                          <HStack>
                            {inDoc && <Badge colorScheme="green" fontSize="2xs" variant="outline">In Doc</Badge>}
                            <Badge colorScheme={color} fontSize="sm">{code}</Badge>
                          </HStack>
                        </Flex>
                      );
                    })}
                    {revedyIcd10.length === 0 && <Text color="gray.500" fontSize="sm">No ICD-10 codes recommended.</Text>}
                  </VStack>
                </CardBody>
              </Card>

            </Flex>

            <Box mt={6}>
              <Flex justify="space-between" align="center" mb={2}>
                <Heading size="sm">ICD-10 & Medical Necessity Chat</Heading>
                <Button 
                  size="sm" 
                  colorScheme="purple" 
                  variant="outline"
                  as="a"
                  href={`/medical-necessity-analysis?caseId=${currentCaseId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Full Medical Necessity Analysis
                </Button>
              </Flex>
              <Text color="gray.600" fontSize="sm" mb={3}>
                Ask questions about the diagnosis codes, sequencing, and medical necessity logic.
              </Text>
              <ModernChat 
                caseId={currentCaseId} 
                contextType="medical_necessity" 
                height="400px"
                collapsedHeight="100px"
              />
            </Box>
          </GridItem>
          
          <GridItem>
            {reportData.icd10_ai_evaluation && (
              <Box p={5} bg="white" shadow="sm" rounded="lg" borderWidth="1px" borderColor="purple.200" h="100%">
                <Heading size="sm" mb={4} color="purple.800">Variance Analysis</Heading>
                
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="gray.700">Discrepancy Summary</Text>
                    <Text fontSize="sm" mt={1}>{reportData.icd10_ai_evaluation.discrepancy_summary}</Text>
                  </Box>
                  <Divider />
                  {reportData.icd10_ai_evaluation.revedy_justification && (
                    <Box p={4} bg="blue.50" rounded="md" borderWidth="1px" borderColor="blue.100">
                      <Text fontWeight="bold" fontSize="sm" color="blue.800" mb={2}>Revedy Justification</Text>
                      <Box fontSize="sm" color="blue.900" sx={markdownStyles}>
                        <ReactMarkdown>{reportData.icd10_ai_evaluation.revedy_justification}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                  
                  {reportData.icd10_ai_evaluation.claim_rationale_hypothesis && (
                    <Box p={4} bg="orange.50" rounded="md" borderWidth="1px" borderColor="orange.100">
                      <Text fontWeight="bold" fontSize="sm" color="orange.800" mb={2}>Claim Rationale Hypothesis</Text>
                      <Box fontSize="sm" color="orange.900" sx={markdownStyles}>
                        <ReactMarkdown>{reportData.icd10_ai_evaluation.claim_rationale_hypothesis}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}
          </GridItem>
        </Grid>
      </Box>
    );
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Container maxW="full" px={6} py={6}>
        
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="lg" mb={1}>Coding Variance Report</Heading>
            <Text color="gray.600">
              Deterministic comparison between CMS 1500 claim and Revedy output.
              {currentCaseId && <Badge ml={2} colorScheme="purple">Case: {currentCaseId}</Badge>}
              {currentClaimType && <Badge ml={2} colorScheme={currentClaimType === 'tech' ? 'purple' : 'blue'}>{currentClaimType.toUpperCase()}</Badge>}
            </Text>
          </Box>
          <HStack spacing={4}>
            {currentCaseId && (
              <Button 
                colorScheme="purple" 
                variant="outline"
                as="a"
                href={`/ionm-strict-analysis/${currentCaseId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Medical Necessity Analysis
              </Button>
            )}
            <Button 
              colorScheme="blue" 
              onClick={generateReport} 
              isLoading={generating}
              loadingText="Generating..."
            >
              {reportData ? "Regenerate Report" : "Generate Report"}
            </Button>
            <Button
              colorScheme="green"
              onClick={handleNextCase}
              isDisabled={allReports.length === 0}
            >
              See Next Case
            </Button>
          </HStack>
        </Flex>

        {/* Case Context Card */}
        {currentCaseId && (
          <Card bg="white" borderColor="gray.200" mb={6}>
            <CardHeader p={3} bg="gray.50" borderBottomWidth="1px">
              <Heading size="sm">Case Context</Heading>
            </CardHeader>
            <CardBody p={4}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">PAYER</Text>
                  <Text fontSize="lg">
                    {workflowStatus?.step2_carrier_assignment?.carrier_name || 'Not specified'} 
                    {workflowStatus?.step2_carrier_assignment?.payer_type ? ` (${workflowStatus.step2_carrier_assignment.payer_type})` : ''}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">STATE</Text>
                  <Text fontSize="lg">{workflowStatus?.step2_carrier_assignment?.state || 'Not specified'}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">CLAIM TYPE</Text>
                  <Text fontSize="lg">{currentClaimType === 'pro' ? 'Professional (26)' : 'Technical (TC)'}</Text>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* Feedback Section */}
        {reportData && (
          <Card bg="white" borderColor="gray.200" mb={6}>
            <CardHeader p={3} bg="gray.50" borderBottomWidth="1px">
              <Flex justify="space-between" align="center">
                <Heading size="sm">Reviewer Feedback</Heading>
                <Button 
                  size="sm" 
                  colorScheme="blue" 
                  onClick={handleSaveFeedback}
                  isLoading={isSavingFeedback}
                >
                  Save Notes
                </Button>
              </Flex>
            </CardHeader>
            <CardBody p={4}>
              <VStack align="stretch" spacing={4}>
                {existingFeedback && existingFeedback.notes && (
                  <Box p={3} bg="blue.50" rounded="md" borderWidth="1px" borderColor="blue.100">
                    <Text fontSize="xs" color="blue.600" fontWeight="bold" mb={1}>
                      LAST SAVED: {new Date(existingFeedback.created_at).toLocaleString()}
                    </Text>
                    <Text fontSize="sm" whiteSpace="pre-wrap">{existingFeedback.notes}</Text>
                  </Box>
                )}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>
                    {existingFeedback ? "Update Notes:" : "Add Notes:"}
                  </Text>
                  <Textarea 
                    placeholder="Enter your review notes, feedback, or explanations for this variance report..."
                    value={feedbackNotes}
                    onChange={(e) => setFeedbackNotes(e.target.value)}
                    rows={3}
                  />
                </Box>
              </VStack>
            </CardBody>
          </Card>
        )}

        {loading ? (
          <Flex justify="center" align="center" p={20}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : !reportData ? (
          <Box bg="white" p={10} rounded="lg" shadow="sm" textAlign="center" borderWidth="1px">
            {generationError ? (
              <VStack spacing={4}>
                <Text color="red.500" fontWeight="bold" fontSize="lg">Variance Cannot Be Calculated</Text>
                <Text color="gray.600">{generationError}</Text>
                <Button colorScheme="blue" variant="outline" onClick={generateReport} isLoading={generating} mt={2}>
                  Try Again
                </Button>
              </VStack>
            ) : (
              <>
                <Text color="gray.500" mb={4}>No variance report found for this case.</Text>
                <Button colorScheme="blue" onClick={generateReport} isLoading={generating}>
                  Generate Now
                </Button>
              </>
            )}
          </Box>
        ) : (
          <Box>
            <Text fontSize="sm" color="gray.500" mb={4}>
              Report generated on: {new Date(reportData.created_at).toLocaleString()}
            </Text>
            
            {renderCPTSection()}
            {renderICD10Section()}

            {/* Payer Denial Impact Analysis */}
            {reportData.payer_denial_evaluation && (
              <Box mt={8} mb={8}>
                <Heading size="md" mb={4}>Payer Denial Impact Analysis</Heading>
                <Card bg="white" borderColor="gray.200" shadow="sm">
                  <CardHeader bg="red.50" py={3} borderBottomWidth="1px" borderColor="red.100">
                    <Heading size="sm" color="red.800">Would Revedy's Coding Have Prevented Denials?</Heading>
                  </CardHeader>
                  <CardBody>
                    {reportData.payer_denial_evaluation.status === 'error' ? (
                      <Text color="red.500">Error evaluating payer denial impact: {reportData.payer_denial_evaluation.message}</Text>
                    ) : (
                      <VStack align="stretch" spacing={6}>
                        <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
                          <Text fontWeight="bold" mb={2}>Overall Summary</Text>
                          <Text fontSize="sm">{reportData.payer_denial_evaluation.overall_summary}</Text>
                        </Box>
                        
                        {reportData.payer_denial_evaluation.evaluated_lines && reportData.payer_denial_evaluation.evaluated_lines.length > 0 ? (
                          <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                              <Thead bg="gray.50">
                                <Tr>
                                  <Th>CPT Code</Th>
                                  <Th>Billed Modifiers</Th>
                                  <Th>Denial Reason (EOB)</Th>
                                  <Th>AI Mapping & Pathway</Th>
                                  <Th>Revedy Proposed Changes</Th>
                                  <Th>Impact</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {reportData.payer_denial_evaluation.evaluated_lines.map((line, idx) => {
                                  // Find the corresponding line item from the raw EOB data to get the mapping info
                                  const rawLineItem = reportData.eob_data?.line_items?.find(item => item.cpt_code === line.cpt_code);
                                  
                                  return (
                                    <Tr key={idx}>
                                      <Td fontWeight="bold">{line.cpt_code}</Td>
                                      <Td>{renderModifiers(line.modifiers_billed)}</Td>
                                      <Td fontSize="sm" color="red.600">{line.denial_reason}</Td>
                                      <Td verticalAlign="top" maxW="200px">
                                        <VStack align="start" spacing={1}>
                                          {rawLineItem?.mapped_carcs && rawLineItem.mapped_carcs.length > 0 ? (
                                            rawLineItem.mapped_carcs.map((mc, i) => (
                                              <Badge key={i} colorScheme="purple" title={mc.reasoning}>
                                                CARC: {mc.code} ({(mc.confidence * 100).toFixed(0)}%)
                                              </Badge>
                                            ))
                                          ) : (
                                            <Text fontSize="xs" color="gray.400">-</Text>
                                          )}
                                          {rawLineItem?.dispute_pathway && rawLineItem.dispute_pathway !== 'standard_appeal' && (
                                            <Badge colorScheme="red">{rawLineItem.dispute_pathway.toUpperCase()}</Badge>
                                          )}
                                          {rawLineItem?.legal_mandate && (
                                            <Badge colorScheme="blue">{rawLineItem.legal_mandate}</Badge>
                                          )}
                                        </VStack>
                                      </Td>
                                      <Td fontSize="sm" color="blue.600">{line.revedy_proposed_changes}</Td>
                                      <Td>
                                        <VStack align="start" spacing={1}>
                                          <Badge colorScheme={line.would_revedy_make_difference ? "green" : "gray"}>
                                            {line.would_revedy_make_difference ? "Potential Mitigation" : "No Change"}
                                          </Badge>
                                          <Text fontSize="xs" color="gray.600">{line.explanation}</Text>
                                        </VStack>
                                      </Td>
                                    </Tr>
                                  );
                                })}
                              </Tbody>
                            </Table>
                          </Box>
                        ) : (
                          <Text fontSize="sm" color="gray.500" fontStyle="italic">No unpaid lines found to evaluate.</Text>
                        )}
                      </VStack>
                    )}
                  </CardBody>
                </Card>
              </Box>
            )}

            {/* Bottom Section: Document Viewer */}
            <Box mt={10}>
              <Card bg="white" borderColor="gray.200" h="1200px">
                <CardHeader p={4} bg="gray.50" borderBottomWidth="1px">
                  <Heading size="sm">Source Document Viewer</Heading>
                </CardHeader>
                <CardBody p={0} h="calc(100% - 53px)">
                  <Grid templateColumns="30% 70%" h="100%">
                    {/* Left Sidebar: Document List */}
                    <GridItem overflowY="auto" borderRightWidth="1px" borderColor="gray.200" bg="white" p={4}>
                      <Box mb={6}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2} borderBottomWidth="1px" pb={1}>
                          Clinical Documentation
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {caseFiles.filter(f => f.document_category === 'ClinicalDocumentation').length > 0 ? (
                            caseFiles.filter(f => f.document_category === 'ClinicalDocumentation').map(file => (
                              <Box
                                key={file.id}
                                p={3}
                                cursor="pointer"
                                borderRadius="md"
                                bg={selectedFileId === file.id ? "blue.50" : "transparent"}
                                _hover={{ bg: "gray.50" }}
                                borderWidth="1px"
                                borderColor={selectedFileId === file.id ? "blue.200" : "transparent"}
                                onClick={() => handleFileSelect(file.id)}
                              >
                                <Flex justify="space-between" align="start">
                                  <VStack align="start" spacing={1} flex="1">
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={2} title={file.file_name}>
                                      {file.file_name}
                                    </Text>
                                    <HStack>
                                      <Tag size="sm" colorScheme="blue" variant="subtle">
                                        <TagLabel fontSize="xs">{file.file_type}</TagLabel>
                                      </Tag>
                                      <Text fontSize="xs" color="gray.500">
                                        {formatFileSize(file.file_size)}
                                      </Text>
                                    </HStack>
                                  </VStack>
                                  <HStack spacing={1}>
                                    <IconButton
                                      icon={<ViewIcon />}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      aria-label="View file text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTextFile(file);
                                        onTextModalOpen();
                                      }}
                                    />
                                    <IconButton
                                      icon={<DownloadIcon />}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      aria-label="Download file"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleFileDownload(file);
                                      }}
                                    />
                                  </HStack>
                                </Flex>
                              </Box>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No clinical documents found.</Text>
                          )}
                        </VStack>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2} borderBottomWidth="1px" pb={1}>
                          Payer Response
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {caseFiles.filter(f => f.document_category === 'PayerResponse').length > 0 ? (
                            caseFiles.filter(f => f.document_category === 'PayerResponse').map(file => (
                              <Box
                                key={file.id}
                                p={3}
                                cursor="pointer"
                                borderRadius="md"
                                bg={selectedFileId === file.id ? "blue.50" : "transparent"}
                                _hover={{ bg: "gray.50" }}
                                borderWidth="1px"
                                borderColor={selectedFileId === file.id ? "blue.200" : "transparent"}
                                onClick={() => handleFileSelect(file.id)}
                              >
                                <Flex justify="space-between" align="start">
                                  <VStack align="start" spacing={1} flex="1">
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={2} title={file.file_name}>
                                      {file.file_name}
                                    </Text>
                                    <HStack>
                                      <Tag size="sm" colorScheme="blue" variant="subtle">
                                        <TagLabel fontSize="xs">{file.file_type}</TagLabel>
                                      </Tag>
                                      <Text fontSize="xs" color="gray.500">
                                        {formatFileSize(file.file_size)}
                                      </Text>
                                    </HStack>
                                  </VStack>
                                  <HStack spacing={1}>
                                    <IconButton
                                      icon={<ViewIcon />}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      aria-label="View file text"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTextFile(file);
                                        onTextModalOpen();
                                      }}
                                    />
                                    <IconButton
                                      icon={<DownloadIcon />}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      aria-label="Download file"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleFileDownload(file);
                                      }}
                                    />
                                  </HStack>
                                </Flex>
                              </Box>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">No payer response documents found.</Text>
                          )}
                        </VStack>
                      </Box>
                    </GridItem>

                    {/* Right Side: Document Viewer */}
                    <GridItem position="relative" bg="gray.100" h="100%">
                      {isDocLoading ? (
                        <Flex justify="center" align="center" h="100%">
                          <VStack>
                            <Spinner size="xl" color="blue.500" />
                            <Text>Loading Document...</Text>
                          </VStack>
                        </Flex>
                      ) : docUrl ? (
                        <iframe 
                          src={docUrl} 
                          title="Document Viewer"
                          width="100%" 
                          height="100%" 
                          style={{ border: 'none' }} 
                        />
                      ) : (
                        <Flex justify="center" align="center" h="100%" color="gray.500" flexDirection="column" p={6} textAlign="center">
                          <Text fontSize="lg" mb={2}>No Document Selected</Text>
                          <Text fontSize="sm">Click a document from the list on the left to view it.</Text>
                        </Flex>
                      )}
                    </GridItem>
                  </Grid>
                </CardBody>
              </Card>
            </Box>
          </Box>
        )}

      </Container>

      {/* File Content Modal */}
      <Modal isOpen={isTextModalOpen} onClose={onTextModalClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {selectedTextFile?.file_name}
            <Tag size="sm" colorScheme="blue" ml={2} mt={-1}>
              <TagLabel>{selectedTextFile?.file_type}</TagLabel>
            </Tag>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg="gray.50" 
              maxH="500px" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {selectedTextFile?.file_text || "No content available"}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              {selectedTextFile ? formatFileSize(selectedTextFile.file_size) : ""} • 
              {selectedTextFile ? " Uploaded " + (selectedTextFile.upload_date ? new Date(selectedTextFile.upload_date).toLocaleDateString() : "Unknown") : ""}
            </Text>
            <Button colorScheme="blue" mr={3} onClick={onTextModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
